// Legacy: pro-frontend/src/app/portfolio/portfolio.component.ts
// resolvePrimaryColor / resolveThemeSelection / parseTemplateConfig

import type { PortfolioResume, PortfolioTemplateSelection } from '../types';

type TemplateColorOption = {
  name?: string;
  className?: string;
  primaryColor?: string;
};

type ParsedTemplateConfig = {
  colorConfig?: {
    colorOptions?: TemplateColorOption[];
    defaultColor?: string;
  };
  themeConfig?: {
    defaultTheme?: string;
  };
};

export function parseTemplateConfig(
  template: PortfolioResume['template'] | null | undefined,
): ParsedTemplateConfig | null {
  if (!template || !template.config) {
    return null;
  }
  if (typeof template.config === 'object') {
    return template.config as ParsedTemplateConfig;
  }
  try {
    return JSON.parse(String(template.config)) as ParsedTemplateConfig;
  } catch {
    return null;
  }
}

export function resolveThemeSelection(
  templateSelection: PortfolioTemplateSelection | null | undefined,
  template: PortfolioResume['template'] | null | undefined,
): 'light' | 'dark' {
  const sel = templateSelection && templateSelection.selected_theme
    ? templateSelection.selected_theme
    : '';
  if (sel === 'dark' || sel === 'light') {
    return sel;
  }
  const config = parseTemplateConfig(template);
  const themeConfig = config && config.themeConfig ? config.themeConfig : null;
  const defaultTheme =
    themeConfig && themeConfig.defaultTheme ? themeConfig.defaultTheme : 'light';
  return defaultTheme === 'dark' ? 'dark' : 'light';
}

export const KNOWN_COLOR_MAP: Record<string, string> = {
  teal: '#39b6a7',
  blue: '#3b82f6',
  purple: '#8b5cf6',
  orange: '#ff6e00',
  green: '#22c55e',
  yellow: '#f59e0b',
  red: '#ef4444',
};

export function resolvePrimaryColor(
  templateSelection: PortfolioTemplateSelection | null | undefined,
  template: PortfolioResume['template'] | null | undefined,
  fallback = '#39b6a7',
  customColorOptions?: Array<{ name?: string; primaryColor?: string; className?: string }>,
): string {
  const selected =
    templateSelection && templateSelection.selected_color
      ? String(templateSelection.selected_color)
      : '';
  if (selected && selected.startsWith('#')) {
    return selected;
  }

  const config = parseTemplateConfig(template);
  const colorConfig = config && config.colorConfig ? config.colorConfig : null;
  const colorOptions =
    colorConfig && Array.isArray(colorConfig.colorOptions) ? colorConfig.colorOptions : [];

  const allOptions = [...colorOptions, ...(customColorOptions || [])];

  if (selected && allOptions.length) {
    const match = allOptions.find(
      (c) =>
        c &&
        (c.name === selected ||
          c.primaryColor === selected ||
          c.className === selected ||
          (c.className && c.className.includes(selected))),
    );
    if (match && match.primaryColor) {
      return match.primaryColor;
    }
  }

  if (selected) {
    const knownColor = KNOWN_COLOR_MAP[selected.toLowerCase()];
    if (knownColor) {
      return knownColor;
    }
  }

  const defaultColorName =
    colorConfig && colorConfig.defaultColor ? colorConfig.defaultColor : '';
  if (defaultColorName && allOptions.length) {
    const def = allOptions.find((c) => c && c.name === defaultColorName);
    if (def && def.primaryColor) {
      return def.primaryColor;
    }
  }

  if (defaultColorName) {
    const defaultColor = KNOWN_COLOR_MAP[defaultColorName.toLowerCase()];
    if (defaultColor) {
      return defaultColor;
    }
  }

  return fallback;
}
